import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Wallet, CreditCard, CheckCircle2 } from 'lucide-react';
import { bc } from '../lib/api';
import { useCart } from '../lib/cart';
import { useAuth } from '../lib/auth';

const loadRzp = () => new Promise((res, rej) => {
  if (window.Razorpay) return res();
  const s = document.createElement('script'); s.src = 'https://checkout.razorpay.com/v1/checkout.js'; s.onload = res; s.onerror = rej; document.head.appendChild(s);
});
const rs = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const FIELDS = [['full_name', 'Full name'], ['phone', 'Mobile (10 digits)'], ['line1', 'House, street, area'], ['landmark', 'Landmark (optional)'], ['city', 'City / town'], ['state', 'State'], ['pincode', 'PIN code']];

function Done({ res }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center space-y-4" data-testid="india-checkout-done">
      <CheckCircle2 className="w-12 h-12 mx-auto text-[#1B5E20]"/>
      <h1 className="font-heading font-black text-3xl">Order placed!</h1>
      <p className="text-ink/70">{res.orders.length} seller{res.orders.length === 1 ? '' : 's'} will pack and ship your items. You'll get tracking details for each.</p>
      <div className="space-y-2 text-left">
        {res.orders.map(o => <Link key={o.id} to={`/orders/${o.id}`} data-testid={`india-done-order-${o.id}`} className="card-soft p-3 flex justify-between"><span>{o.store_name}</span><b>{rs(o.total)}</b></Link>)}
      </div>
    </div>
  );
}

export default function IndiaCheckout() {
  const cart = useCart();
  const { user } = useAuth();
  const [a, setA] = useState({ full_name: user?.name || '', phone: user?.phone || '', line1: '', landmark: '', city: '', state: '', pincode: '' });
  const [method, setMethod] = useState('razorpay');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [pending, setPending] = useState(null);

  if (done) return <Done res={done}/>;
  if (!user) return <div className="p-10 text-center" data-testid="india-checkout-login"><button className="btn-primary" onClick={() => window.dispatchEvent(new CustomEvent('mandispot:open-auth', { detail: { mode: 'signin' } }))}>Sign in to checkout</button></div>;
  if (!cart.india.length) return <Navigate to="/cart" replace/>;

  const pay = async (res) => {
    await loadRzp();
    return new Promise((resolve, reject) => {
      new window.Razorpay({
        key: res.razorpay.key_id, amount: res.razorpay.amount, currency: 'INR', order_id: res.razorpay.razorpay_order_id,
        name: 'BharatCart', description: `${res.orders.length} seller order(s)`, prefill: { name: a.full_name, contact: `+91${a.phone}` }, theme: { color: '#E67E22' },
        handler: async (r) => { try { await bc.verifyGroup(res.group_id, r); resolve(); } catch (e) { reject(e); } },
        modal: { ondismiss: () => reject(new Error('Payment not completed. Tap "Pay now" to try again.')) },
      }).open();
    });
  };

  const place = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (!/^\d{10}$/.test(a.phone)) throw new Error('Enter a 10-digit mobile number');
      if (!/^\d{6}$/.test(a.pincode)) throw new Error('Enter a 6-digit PIN code');
      const res = pending || await bc.checkout({ items: cart.india.map(i => ({ product_id: i.product.id, quantity: i.qty })), address: a, payment_method: method });
      if (res.razorpay) { setPending(res); await pay(res); }
      cart.indiaClear(); setPending(null); setDone(res); toast.success('Order placed');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((Array.isArray(d) ? d[0]?.msg : d) || err.message || 'Could not place order');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={place} className="max-w-4xl mx-auto px-4 md:px-6 py-8 grid md:grid-cols-[1fr_320px] gap-6" data-testid="india-checkout-page">
      <div className="space-y-4">
        <h1 className="font-heading font-black text-3xl">Checkout</h1>
        <div className="card-soft p-4 grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <label key={k} className={k === 'line1' ? 'sm:col-span-2' : ''}><span className="text-xs text-ink/60">{l}</span>
              <input data-testid={`india-addr-${k}`} value={a[k]} required={k !== 'landmark'} disabled={!!pending} inputMode={k === 'phone' || k === 'pincode' ? 'numeric' : undefined} maxLength={k === 'phone' ? 10 : k === 'pincode' ? 6 : undefined} onChange={e => setA({ ...a, [k]: e.target.value })} className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"/></label>
          ))}
        </div>
        <div className="card-soft p-4 space-y-2">
          <div className="font-semibold">Payment</div>
          {[['razorpay', 'Pay online (UPI, cards, netbanking)', CreditCard], ['cod', 'Cash on delivery', Wallet]].map(([v, l, Icon]) => (
            <label key={v} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${method === v ? 'border-[#E67E22] bg-[#FFF8EE]' : 'border-border'}`}>
              <input type="radio" data-testid={`india-pay-${v}`} checked={method === v} disabled={!!pending} onChange={() => setMethod(v)}/><Icon className="w-4 h-4"/>{l}
            </label>
          ))}
        </div>
      </div>
      <aside className="card-soft p-4 h-fit space-y-2 md:sticky md:top-24">
        <div className="font-semibold">{cart.india.length} item{cart.india.length === 1 ? '' : 's'}</div>
        {cart.india.map(i => <div key={i.product.id} className="flex justify-between text-sm"><span className="truncate pr-2">{i.qty} × {i.product.name}</span><span>{rs(i.product.price * i.qty)}</span></div>)}
        <div className="border-t border-border pt-2 flex justify-between font-bold"><span>Items total</span><span data-testid="india-checkout-subtotal">{rs(cart.totals.indiaSubtotal)}</span></div>
        <p className="text-xs text-ink/60">Each seller's delivery fee and any welcome offer are added per order.</p>
        <button data-testid="india-place-order-btn" disabled={busy} className="btn-primary w-full">{busy && <Loader2 className="w-4 h-4 animate-spin"/>}{pending ? 'Pay now' : 'Place order'}</button>
        {pending && <p className="text-xs text-ink/60" data-testid="india-pending-note">Your orders are reserved. Complete payment to confirm them.</p>}
      </aside>
    </form>
  );
}
